import { getRequiredAccessToken } from "./client";
import { apiUrl, responseSnippet } from "./base";

/**
 * Reach (A3-T2) — the user's own hand on everything Nami says out of band, and
 * the evidence that any of it landed.
 *
 * Uses a dedicated fetch rather than `authedFetch` for the settings read: while
 * NAMI_REACH_V1 is off the routes are not registered, and that 404 is a TYPED
 * outcome ("this build has no notifications to configure") the section reads to
 * hide itself. `authedFetch` turns every non-2xx into a thrown Error, which would
 * make "feature off" indistinguishable from "the server is unwell" without
 * string-matching a message — which we never do.
 */

/** Mirrors the server contract; defined here so web and server stay decoupled. */
export interface ReachSettings {
  enabled: boolean;
  emailFallback: boolean;
  /** 'HH:MM' in the user's own timezone. */
  quietStart: string;
  quietEnd: string;
}

export type ReachSettingsStatus =
  | { available: false }
  | {
      available: true;
      settings: ReachSettings;
      browserPermissionHint: boolean;
      /** False while the server's email leg is off (NAMI_REACH_EMAIL_V1): the toggle would promise nothing. */
      emailAvailable: boolean;
    };

/** One past notification, as the replay leg reads it: the wire frame plus whether it landed. */
export interface RecentReach {
  id: string;
  kind: string;
  title: string;
  body: string;
  deepLink: string;
  politeness: string;
  createdAt: string;
  seenAt: string | null;
}

const SETTINGS_PATH = "/api/reach/settings";

async function authHeader(): Promise<Record<string, string>> {
  const token = await getRequiredAccessToken();
  return { Authorization: `Bearer ${token}` };
}

interface SettingsBody extends ReachSettings {
  browserPermissionHint?: boolean;
  emailAvailable?: boolean;
}

function readSettings(body: SettingsBody): {
  settings: ReachSettings;
  browserPermissionHint: boolean;
  emailAvailable: boolean;
} {
  return {
    settings: {
      enabled: body.enabled,
      emailFallback: body.emailFallback,
      quietStart: body.quietStart,
      quietEnd: body.quietEnd,
    },
    browserPermissionHint: body.browserPermissionHint === true,
    // A server that predates the field has email: absent reads as available.
    emailAvailable: body.emailAvailable !== false,
  };
}

/** GET settings. 404 (flag off) => not available, and the section renders nothing. */
export async function getReachSettings(): Promise<ReachSettingsStatus> {
  const res = await fetch(apiUrl(SETTINGS_PATH), {
    headers: await authHeader(),
    credentials: "include",
  });
  if (res.status === 404) return { available: false };
  if (!res.ok) throw new Error(`${res.status} ${await responseSnippet(res)}`);
  return {
    available: true,
    ...readSettings((await res.json()) as SettingsBody),
  };
}

/**
 * PATCH settings. Partial: keys the patch omits are left untouched server-side.
 * Any non-2xx throws — including the 404 of a flag flipped while the page was
 * open, which is a failed save like any other and must not read as success.
 */
export async function updateReachSettings(
  patch: Partial<ReachSettings>
): Promise<ReachSettings> {
  const res = await fetch(apiUrl(SETTINGS_PATH), {
    method: "PATCH",
    headers: { ...(await authHeader()), "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`${res.status} ${await responseSnippet(res)}`);
  return readSettings((await res.json()) as SettingsBody).settings;
}

/**
 * POST seen — the ONLY evidence the user was actually reached. Sent on a click,
 * or when a composer notice arrived about the very task they were already
 * looking at in a visible tab (the screen beacon's question, asked by
 * `taskOnScreen`): that screen is showing those words already, so it is a
 * sighting the same way a click is.
 *
 * Never on a mere RENDER, which is the line that matters: a notification that
 * merely painted somewhere is exactly what socket_emitted_at already records.
 */
export async function markReachSeen(id: string): Promise<void> {
  const res = await fetch(apiUrl(`/api/reach/${encodeURIComponent(id)}/seen`), {
    method: "POST",
    headers: await authHeader(),
    credentials: "include",
  });
  if (!res.ok) throw new Error(`${res.status} ${await responseSnippet(res)}`);
}

/**
 * POST dismissed — the user waved it away. Deliberately NOT `markReachSeen`:
 * dismissing is a refusal, not a sighting, so seen_at stays null and the email
 * leg keeps its claim on a row nobody has actually read.
 */
export async function dismissReach(id: string): Promise<void> {
  const res = await fetch(
    apiUrl(`/api/reach/${encodeURIComponent(id)}/dismissed`),
    {
      method: "POST",
      headers: await authHeader(),
      credentials: "include",
    }
  );
  if (!res.ok) throw new Error(`${res.status} ${await responseSnippet(res)}`);
}

/** GET recent — the owner's last N, for a surface that was not open when they went out. */
export async function getRecentReach(limit?: number): Promise<RecentReach[]> {
  const query = limit ? `?limit=${encodeURIComponent(String(limit))}` : "";
  const res = await fetch(apiUrl(`/api/reach/recent${query}`), {
    headers: await authHeader(),
    credentials: "include",
  });
  if (res.status === 404) return [];
  if (!res.ok) throw new Error(`${res.status} ${await responseSnippet(res)}`);
  const body = (await res.json()) as { notifications?: RecentReach[] };
  return body.notifications ?? [];
}
